import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, KeyRound, ShieldCheck, PlugZap, ScrollText, CheckCircle2, XCircle, RefreshCw, FlaskConical, Trash2 } from "lucide-react";

const BASE_LABELS = { oauth: "OAuth", realestate: "Real Estate", cap: "CAP", catasto: "Catasto / Ipotecaria", camerale: "Visure Camerali" };

export default function AdminPanel() {
  return (
    <div className="space-y-6" data-testid="admin-panel">
      <div>
        <h1 className="text-3xl font-light text-white flex items-center gap-3"><ShieldCheck className="w-7 h-7 text-[#EA7B2B]" /> Amministrazione</h1>
        <p className="text-sm text-zinc-500 mt-1">Integrazioni API e registro attività (Audit / GDPR)</p>
      </div>
      <Integrations />
      <SpendingLimits />
      <DemoData />
      <AuditLog />
    </div>
  );
}

function DemoData() {
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/demo/count").then((r) => setCount(r.data.operazioni_demo)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const purge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/demo/purge");
      toast.success(`Dati demo eliminati: ${data.operazioni_eliminate} operazioni, ${data.dd_requests_eliminate} due diligence, ${data.notifiche_eliminate} notifiche`);
      setConfirming(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Dati demo · ambiente di test" icon={FlaskConical} testid="admin-demo-data">
      <p className="text-sm text-zinc-400 mb-4">
        Sono presenti <b className="text-white" data-testid="demo-count">{count ?? "—"}</b> operazioni dimostrative (badge DEMO).
        Prima del passaggio in produzione, elimina tutti i dati demo (operazioni, richieste di Due Diligence e notifiche collegate).
        <br /><span className="text-[#E11D48]">Azione irreversibile.</span>
      </p>
      {!confirming ? (
        <button onClick={() => setConfirming(true)} disabled={!count} data-testid="demo-purge-btn"
          className="flex items-center gap-2 bg-[#E11D48]/10 border border-[#E11D48]/40 text-[#E11D48] text-sm font-medium px-4 py-2.5 rounded-sm hover:bg-[#E11D48]/20 transition-colors disabled:opacity-40">
          <Trash2 className="w-4 h-4" /> Elimina tutti i dati demo
        </button>
      ) : (
        <div className="flex items-center gap-2">
          <span className="text-sm text-zinc-300">Confermi l'eliminazione di {count} operazioni demo?</span>
          <button onClick={purge} disabled={busy} data-testid="demo-purge-confirm"
            className="flex items-center gap-2 bg-[#E11D48] text-white text-sm font-medium px-4 py-2 rounded-sm hover:bg-[#c01840] transition-colors disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Sì, elimina
          </button>
          <button onClick={() => setConfirming(false)} disabled={busy}
            className="text-sm text-zinc-400 px-3 py-2 rounded-sm hover:bg-white/5 transition-colors">Annulla</button>
        </div>
      )}
    </Card>
  );
}

function Card({ title, icon: Icon, children, action, testid }) {
  return (
    <div className="bg-[#122A3F] border border-white/10 rounded-sm" data-testid={testid}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
        <h2 className="text-sm font-medium text-zinc-200 uppercase tracking-wider flex items-center gap-2">{Icon && <Icon className="w-4 h-4 text-[#EA7B2B]" />} {title}</h2>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Integrations() {
  const [st, setSt] = useState(null);
  const [form, setForm] = useState({ env: "sandbox", email: "", api_key: "", api_key_prod: "", token: "", bases: {} });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testRes, setTestRes] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/integrations").then((r) => {
      setSt(r.data.openapi);
      setForm((f) => ({ ...f, env: r.data.openapi.env, email: r.data.openapi.email || "", bases: r.data.openapi.bases || {} }));
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    const payload = { env: form.env, bases: form.bases };
    if (form.email) payload.email = form.email;
    if (form.api_key) payload.api_key = form.api_key;
    if (form.api_key_prod) payload.api_key_prod = form.api_key_prod;
    if (form.token !== "") payload.token = form.token;
    try {
      await api.put("/admin/integrations/openapi", payload);
      toast.success("Credenziali OpenAPI aggiornate");
      setForm((f) => ({ ...f, api_key: "", api_key_prod: "", token: "" }));
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true); setTestRes(null);
    try {
      const { data } = await api.post("/admin/integrations/openapi/test");
      setTestRes(data);
      toast[data.ok ? "success" : "error"](data.ok ? "Credenziali valide" : "Credenziali non valide");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  if (!st) return null;
  const inp = "w-full bg-[#0A1826] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#EA7B2B]";
  return (
    <Card title="Integrazioni · OpenAPI.com" icon={PlugZap} testid="admin-integrations"
      action={
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 text-[11px] ${st.configured ? "text-[#10B981]" : "text-[#F87171]"}`}>
            {st.configured ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />} {st.configured ? "Configurata" : "Non configurata"}
          </span>
          <button onClick={test} disabled={testing} data-testid="integration-test-btn" className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm border border-white/15 text-white hover:border-[#EA7B2B]/60 disabled:opacity-50">
            {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Verifica
          </button>
        </div>
      }>
      <p className="text-[12px] text-zinc-500 mb-4">Le chiavi sono salvate in modo sicuro sul server (mai nel repository) e mostrate mascherate. Inserisci un valore solo per modificarlo.</p>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Ambiente">
          <select value={form.env} onChange={(e) => setForm({ ...form, env: e.target.value })} data-testid="integration-env" className={inp}>
            <option value="sandbox">Sandbox (test)</option>
            <option value="production">Produzione</option>
          </select>
        </Field>
        <Field label="Email account OpenAPI">
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="integration-email" className={inp} />
        </Field>
        <Field label={`API Key sandbox ${st.api_key_masked ? `(attuale: ${st.api_key_masked})` : ""}`}>
          <input type="password" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} placeholder="•••• inserisci per modificare" data-testid="integration-apikey" className={inp} />
        </Field>
        <Field label="API Key produzione">
          <input type="password" value={form.api_key_prod} onChange={(e) => setForm({ ...form, api_key_prod: e.target.value })} placeholder="•••• opzionale" data-testid="integration-apikey-prod" className={inp} />
        </Field>
        <Field label={`Bearer token (opzionale) ${st.token_masked ? `(attuale: ${st.token_masked})` : ""}`}>
          <input type="password" value={form.token} onChange={(e) => setForm({ ...form, token: e.target.value })} placeholder="•••• alternativa alla API key" data-testid="integration-token" className={inp} />
        </Field>
      </div>
      <div className="mt-4">
        <div className="text-[11px] uppercase text-zinc-500 mb-2">Base URL (avanzato · modificabili quando cambi provider)</div>
        <div className="grid md:grid-cols-2 gap-2">
          {Object.keys(BASE_LABELS).map((k) => (
            <div key={k} className="flex items-center gap-2">
              <label className="text-xs text-zinc-400 w-28 shrink-0">{BASE_LABELS[k]}</label>
              <input value={form.bases[k] || ""} onChange={(e) => setForm({ ...form, bases: { ...form.bases, [k]: e.target.value } })} data-testid={`base-${k}`} className={inp} />
            </div>
          ))}
        </div>
      </div>
      {testRes && (
        <div className={`mt-4 text-[12px] rounded-sm p-2.5 border ${testRes.ok ? "text-[#10B981] border-[#10B981]/30 bg-[#10B981]/5" : "text-[#F87171] border-[#F87171]/30 bg-[#F87171]/5"}`} data-testid="integration-test-result">
          {testRes.messaggio}
        </div>
      )}
      <div className="mt-4 flex justify-end">
        <button onClick={save} disabled={saving} data-testid="integration-save-btn" className="flex items-center gap-2 bg-[#EA7B2B] text-black text-sm font-medium px-4 py-2 rounded-sm hover:bg-[#F59E4A] disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Salva credenziali
        </button>
      </div>
    </Card>
  );
}

function Field({ label, children }) {
  return <div><label className="text-[11px] uppercase text-zinc-500 block mb-1">{label}</label>{children}</div>;
}

const eur = (v) => `€ ${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function SpendingLimits() {
  const [users, setUsers] = useState([]);
  const [edits, setEdits] = useState({});
  const [savingId, setSavingId] = useState("");

  const load = useCallback(() => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (u) => {
    setSavingId(u.id);
    const raw = edits[u.id];
    const limit = raw === "" || raw == null ? null : parseFloat(raw);
    try {
      await api.put(`/admin/users/${u.id}/spending-limit`, { limit });
      toast.success(`Limite aggiornato per ${u.name}`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingId(""); }
  };

  return (
    <Card title="Limiti di spesa mensili (per utente)" icon={KeyRound} testid="admin-spending-limits">
      <p className="text-[12px] text-zinc-500 mb-3">Imposta il tetto di spesa mensile su servizi premium. Vuoto = nessun limite. Superato il limite, la Due Diligence viene bloccata prima di qualsiasi addebito.</p>
      <table className="w-full text-sm" data-testid="spending-limits-table">
        <thead><tr className="text-zinc-500 text-[11px] uppercase text-left"><th className="py-1.5">Utente</th><th>Ruolo</th><th className="text-right pr-4">Spesa mese</th><th className="text-right">Limite €/mese</th><th></th></tr></thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-t border-white/5" data-testid={`limit-row-${u.id}`}>
              <td className="py-2 text-white">{u.name}</td>
              <td className="text-zinc-400 text-xs">{u.role}</td>
              <td className="text-right pr-4 font-mono text-zinc-300">{eur(u.spesa_mese)}</td>
              <td className="text-right">
                <input type="number" defaultValue={u.spending_limit ?? ""} onChange={(e) => setEdits({ ...edits, [u.id]: e.target.value })}
                  placeholder="—" data-testid={`limit-input-${u.id}`}
                  className="w-24 bg-[#0A1826] border border-white/10 rounded-sm px-2 py-1.5 text-white text-right focus:outline-none focus:ring-1 focus:ring-[#EA7B2B]" />
              </td>
              <td className="text-right pl-2">
                <button onClick={() => save(u)} disabled={savingId === u.id} data-testid={`limit-save-${u.id}`}
                  className="text-xs px-3 py-1.5 rounded-sm border border-white/15 text-white hover:border-[#EA7B2B]/60 disabled:opacity-50">
                  {savingId === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : "Salva"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}


const ACTION_COLOR = { login: "#60A5FA", create: "#10B981", update: "#EA7B2B", delete: "#F87171", execute: "#A78BFA", credit: "#10B981", charge: "#F59E0B" };

function AuditLog() {
  const [items, setItems] = useState([]);
  const [entity, setEntity] = useState("");
  const [action, setAction] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (entity) params.entity = entity;
    if (action) params.action = action;
    api.get("/admin/audit", { params }).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [entity, action]);
  useEffect(() => { load(); }, [load]);

  const sel = "bg-[#0A1826] border border-white/10 rounded-sm px-2.5 py-1.5 text-xs text-white focus:outline-none";
  return (
    <Card title="Audit log · registro attività" icon={ScrollText} testid="admin-audit"
      action={
        <div className="flex items-center gap-2">
          <select value={entity} onChange={(e) => setEntity(e.target.value)} data-testid="audit-filter-entity" className={sel}>
            <option value="">Tutte le entità</option>
            {["deal", "workflow", "crm", "wallet", "due_diligence", "pricing", "integration", "compliance", "user"].map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <select value={action} onChange={(e) => setAction(e.target.value)} data-testid="audit-filter-action" className={sel}>
            <option value="">Tutte le azioni</option>
            {["login", "create", "update", "delete", "execute", "credit"].map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </div>
      }>
      {loading ? <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-[#EA7B2B] inline" /></div> : items.length === 0 ? (
        <p className="text-sm text-zinc-500">Nessuna attività registrata.</p>
      ) : (
        <div className="overflow-auto max-h-[500px]">
          <table className="w-full text-sm" data-testid="audit-table">
            <thead className="sticky top-0 bg-[#122A3F]"><tr className="text-zinc-500 text-[11px] uppercase text-left">
              <th className="py-2 pr-3">Data/ora</th><th className="pr-3">Utente</th><th className="pr-3">Azione</th><th className="pr-3">Entità</th><th className="pr-3">Record</th><th>IP</th>
            </tr></thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-t border-white/5">
                  <td className="py-2 pr-3 text-zinc-400 whitespace-nowrap">{new Date(a.timestamp).toLocaleString("it-IT")}</td>
                  <td className="pr-3 text-white">{a.user_name || a.user_email || "—"}</td>
                  <td className="pr-3"><span className="text-xs font-medium" style={{ color: ACTION_COLOR[a.action] || "#a1a1aa" }}>{a.action}</span></td>
                  <td className="pr-3 text-zinc-300">{a.entity}</td>
                  <td className="pr-3 text-zinc-500 font-mono text-[11px] truncate max-w-[160px]">{a.entity_id || "—"}</td>
                  <td className="text-zinc-500 font-mono text-[11px]">{a.ip || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
